// Dibujo de las capas de Choferes en vivo. Cada función vacía su grupo de
// Leaflet y lo vuelve a llenar con lo que recibe; la pantalla decide cuándo
// llamarlas (pages/MapaChoferesPage). `pick(sel)` da el handler del clic de
// cada cosa (abre su ficha).
//
// Todo va como círculos en el canvas (preferCanvas) salvo lo que lleva un
// símbolo o un número: con todas las capas prendidas son miles de puntos y
// como elementos sueltos el mapa se arrastra. Los tooltips van como texto.
import type { LatLng, LeafletLite, LGroup, LMap } from "./leafletTypes";
import { boxIcon, numberIcon } from "./baseMap";
import { textTooltip } from "./leafletText";
import { Chofer, choferTooltip, colorDe, haceCuanto, llevaTurbo, TURBO } from "./liveDrivers";
import {
  CLUSTER_MAX_ZOOM, gridClusters, homeTooltip, LayerKey, MapHome, MapLayersData, MapSel, MapStop, STATE_COLOR, StopState, stopTooltip,
  TURBO_ESTADO_COLOR, TurboHoy, turboStores, turboTooltip, visibleStops, ZONE_COLORS,
} from "./mapLayers";

export type Pick = (sel: MapSel) => () => void;
export type Grupos = Record<LayerKey | "foco" | "marca", LGroup>;
export type Trazado = { lines: LatLng[][]; straight: boolean; km: number | null };

// Un grupo por capa; prender o apagar es sacarlo o ponerlo. «foco» (lo de un
// chofer) y «marca» (el anillo de lo elegido) van siempre puestos. Orden de
// abajo hacia arriba: las zonas de fondo, los choferes encima de todo.
export function crearGrupos(L: LeafletLite, map: LMap): Grupos {
  const g = {} as Grupos;
  for (const k of ["zones", "stores", "turbo", "deliveries", "pickups", "warehouses", "homes", "drivers", "foco", "marca"] as const) g[k] = L.layerGroup();
  g.foco.addTo(map);
  g.marca.addTo(map);
  return g;
}

// Lo recién dibujado queda arriba en el canvas: los choferes vuelven a subir.
export const subirChoferes = (g: Grupos) => g.drivers.eachLayer((l) => l.bringToFront?.());

// Marcador con un símbolo (depósito, domicilio). Pocos por mapa: los
// cientos de paradas y tiendas van como círculos.
const simbolo = (L: LeafletLite, texto: string, color: string) =>
  boxIcon(L, 22, texto, { borderRadius: "6px", background: color, border: "2px solid #111", fontSize: "12px" });

// Varios puntos agrupados: un círculo (en el canvas, así los choferes quedan
// encima) un poco más grande cuantos más son, con borde rojo si alguno tiene
// un problema, y la cantidad arriba como texto que no toma los clics.
const ladoGrupo = (n: number) => Math.round(26 + Math.min(16, Math.log10(n) * 8));
const grupoNumero = (L: LeafletLite, n: number) =>
  boxIcon(L, ladoGrupo(n), String(n), { color: "#fff", font: "700 12px system-ui,sans-serif", textShadow: "0 1px 2px rgba(0,0,0,.7)", pointerEvents: "none" });

export function dibujarZonas(L: LeafletLite, g: Grupos, data: MapLayersData | null, pick: Pick) {
  g.zones.clearLayers();
  data?.zones.forEach((z, i) => {
    const color = ZONE_COLORS[i % ZONE_COLORS.length];
    L.polygon(z.rings, { color, weight: 2, fillColor: color, fillOpacity: 0.08 })
      .bindTooltip(textTooltip(`Zona ${z.name}`), { sticky: true })
      .on("click", pick({ kind: "zones", id: z.id }))
      .addTo(g.zones);
  });
}

export function dibujarDepositos(L: LeafletLite, g: Grupos, data: MapLayersData | null, pick: Pick) {
  g.warehouses.clearLayers();
  for (const w of data?.warehouses ?? []) {
    L.marker([w.latitude, w.longitude], { icon: simbolo(L, w.type === "DROPOFF" ? "D" : "▣", "#e3b341") })
      .bindTooltip(textTooltip(`${w.type === "DROPOFF" ? "Dropoff" : "Depósito"} · ${w.name}${w.address ? ` (${w.address})` : ""}`))
      .on("click", pick({ kind: "warehouses", id: w.id }))
      .addTo(g.warehouses);
  }
}

export function dibujarDomicilios(L: LeafletLite, g: Grupos, homes: MapHome[], pick: Pick) {
  g.homes.clearLayers();
  for (const h of homes) {
    L.marker([h.latitude, h.longitude], { icon: simbolo(L, "⌂", "#f0883e") })
      .bindTooltip(textTooltip(homeTooltip(h)))
      .on("click", pick({ kind: "homes", id: h.driver_id }))
      .addTo(g.homes);
  }
}

export function dibujarChoferes(L: LeafletLite, g: Grupos, choferes: Chofer[], pick: Pick) {
  g.drivers.clearLayers();
  for (const c of choferes) {
    if (c.latitude == null || c.longitude == null) continue;
    const turbo = llevaTurbo(c);
    L.circleMarker([c.latitude, c.longitude], {
      radius: 6 + (turbo ? 3 : 0), weight: turbo ? 3 : 1,
      color: turbo ? TURBO : "#111", fillOpacity: 0.95, fillColor: colorDe(c),
    })
      .bindTooltip(textTooltip(choferTooltip(c)))
      .on("click", pick({ kind: "drivers", id: c.driver_id }))
      .addTo(g.drivers);
  }
}

// Agrupado en juego: el zoom al que se agrupa y lo elegido (que va siempre
// suelto, para que se vea con su anillo). null: todo suelto.
export type Agrupado = { zoom: number; sel: MapSel | null } | null;

// Tiendas y paradas: con el agrupado, los puntos que caen juntos van como un
// círculo con la cantidad, y tocarlo acerca.
export function dibujarPuntos(L: LeafletLite, map: LMap, g: Grupos, data: MapLayersData | null, states: Record<StopState, boolean>, agrupado: Agrupado, pick: Pick) {
  for (const k of ["stores", "pickups", "deliveries"] as const) g[k].clearLayers();
  if (!data) return;
  const acercar = (pts: LatLng[]) => {
    const mismo = pts.every((p) => p[0] === pts[0][0] && p[1] === pts[0][1]);
    if (mismo) map.setView(pts[0], CLUSTER_MAX_ZOOM); else map.fitBounds(pts, { padding: [40, 40] });
  };
  const dibujar = <T extends { id: number }>(kind: LayerKey, items: T[], pos: (t: T) => LatLng, suelto: (t: T) => void,
    nombre: string, color: string, problema: (t: T) => boolean = () => false) => {
    if (!agrupado) { items.forEach(suelto); return; }
    const resto: { item: T; x: number; y: number }[] = [];
    for (const t of items) {
      if (agrupado.sel?.kind === kind && agrupado.sel.id === t.id) { suelto(t); continue; }
      const px = map.project(pos(t), agrupado.zoom);
      resto.push({ item: t, x: px.x, y: px.y });
    }
    for (const grupo of gridClusters(resto)) {
      if (grupo.length === 1) { suelto(grupo[0]); continue; }
      const pts = grupo.map(pos);
      const centro: LatLng = [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];
      const conProblema = grupo.filter(problema).length;
      L.circleMarker(centro, { radius: ladoGrupo(grupo.length) / 2, weight: 2, color: conProblema ? "#f85149" : "#fff", fillColor: color, fillOpacity: 0.92 })
        .bindTooltip(textTooltip(`${grupo.length} ${nombre}${conProblema ? ` · ${conProblema} con problema` : ""} · tocá para acercar`))
        .on("click", () => acercar(pts))
        .addTo(g[kind]);
      L.marker(centro, { icon: grupoNumero(L, grupo.length), interactive: false, keyboard: false }).addTo(g[kind]);
    }
  };
  dibujar("stores", data.stores, (s) => [s.latitude, s.longitude], (s) => {
    L.circleMarker([s.latitude, s.longitude], { radius: 5, weight: 1, color: "#111", fillColor: "#db61a2", fillOpacity: 0.9 })
      .bindTooltip(textTooltip(`Tienda · ${s.name}${s.address ? ` (${s.address})` : ""}`))
      .on("click", pick({ kind: "stores", id: s.id }))
      .addTo(g.stores);
  }, "tiendas", "#db61a2");
  // Colecta: punto más grande con borde grueso; entrega: punto chico.
  for (const [tipo, kind, radio, borde, nombre, color] of [["PICKUP", "pickups", 7, 3, "colectas", "#a371f7"], ["DELIVERY", "deliveries", 4, 1, "repartos", "#2f81f7"]] as const) {
    dibujar(kind, visibleStops(data.stops, tipo, states), (s) => [s.latitude as number, s.longitude as number], (s) => {
      L.circleMarker([s.latitude, s.longitude], {
        radius: radio, weight: borde, color: tipo === "PICKUP" ? "#a371f7" : "#111",
        fillColor: STATE_COLOR[s.state], fillOpacity: 0.9,
      })
        .bindTooltip(textTooltip(stopTooltip(s)))
        .on("click", pick({ kind, id: s.id }))
        .addTo(g[kind]);
    }, nombre, color, (s) => s.state === "PROBLEM");
  }
}

export type Foco = { trazados: Trazado[]; paradas: MapStop[]; casa: MapHome | null; chofer: Chofer | null; nombre: string };

// Lo de un chofer: el trazado, sus paradas numeradas en orden, su casa y él.
// Devuelve los puntos dibujados, para encuadrarlos.
export function dibujarFoco(L: LeafletLite, g: Grupos, foco: Foco | null, pick: Pick): LatLng[] {
  g.foco.clearLayers();
  const puntos: LatLng[] = [];
  if (!foco) return puntos;
  for (const t of foco.trazados) {
    for (const line of t.lines) L.polyline(line, { color: "#2f81f7", weight: 4, opacity: 0.85, dashArray: t.straight ? "6 6" : undefined }).addTo(g.foco);
  }
  for (const s of foco.paradas) {
    if (s.latitude == null || s.longitude == null) continue;
    puntos.push([s.latitude, s.longitude]);
    L.marker([s.latitude, s.longitude], { icon: numberIcon(L, String(s.sequence), STATE_COLOR[s.state], { textColor: "#111", square: s.type === "PICKUP", weight: 700 }) })
      .bindTooltip(textTooltip(stopTooltip(s)))
      .on("click", pick({ kind: s.type === "PICKUP" ? "pickups" : "deliveries", id: s.id }))
      .addTo(g.foco);
  }
  const { casa, chofer } = foco;
  if (casa) {
    puntos.push([casa.latitude, casa.longitude]);
    L.marker([casa.latitude, casa.longitude], { icon: simbolo(L, "⌂", "#f0883e") })
      .bindTooltip(textTooltip(homeTooltip(casa)))
      .on("click", pick({ kind: "homes", id: casa.driver_id }))
      .addTo(g.foco);
  }
  if (chofer?.latitude != null && chofer.longitude != null) {
    puntos.push([chofer.latitude, chofer.longitude]);
    L.circleMarker([chofer.latitude, chofer.longitude], { radius: 9, weight: 3, color: "#fff", fillColor: colorDe(chofer), fillOpacity: 1 })
      .bindTooltip(textTooltip(`${foco.nombre} · ${haceCuanto(chofer.recorded_at)}`))
      .on("click", pick({ kind: "drivers", id: chofer.driver_id }))
      .addTo(g.foco);
  }
  return puntos;
}

// Anillo sobre lo elegido.
// Turbos de hoy: la tienda con un rayo (y cuántos tiene), el destino de cada
// Turbo como punto del color de su estado y, mientras está abierto, una línea
// punteada de la tienda al destino. El chofer que lo lleva ya se ve en la capa
// de choferes (punto naranja grande).
export function dibujarTurbos(L: LeafletLite, g: Grupos, turbos: TurboHoy[], pick: Pick) {
  g.turbo.clearLayers();
  for (const t of turbos) {
    if (!t.destino) continue;
    const color = TURBO_ESTADO_COLOR[t.estado];
    const destino: LatLng = [t.destino.lat, t.destino.lng];
    if (!t.terminal && t.tienda) {
      L.polyline([[t.tienda.lat, t.tienda.lng], destino], { color, weight: 2, opacity: 0.55, dashArray: "4 6", interactive: false }).addTo(g.turbo);
    }
    L.circleMarker(destino, { radius: t.terminal ? 4 : 7, weight: t.terminal ? 1 : 2, color: t.terminal ? "#111" : "#fff", fillColor: color, fillOpacity: t.terminal ? 0.5 : 0.95 })
      .bindTooltip(textTooltip(turboTooltip(t)))
      .on("click", pick({ kind: "turbo", id: t.delivery_id }))
      .addTo(g.turbo);
  }
  for (const s of turboStores(turbos)) {
    const texto = s.abiertos ? `⚡${s.abiertos}` : "⚡";
    L.marker([s.lat, s.lng], { icon: boxIcon(L, 26, texto, { borderRadius: "6px", background: s.abiertos ? TURBO : "#8b949e", border: "2px solid #111", fontSize: "12px", color: "#111", fontWeight: "700" }) })
      .bindTooltip(textTooltip(`Tienda · ${s.name} · ${s.total} Turbo${s.total === 1 ? "" : "s"} hoy${s.abiertos ? ` (${s.abiertos} abierto${s.abiertos === 1 ? "" : "s"})` : " (todos cerrados)"}`))
      .on("click", pick({ kind: "stores", id: s.id }))
      .addTo(g.turbo);
  }
}

export function dibujarMarca(L: LeafletLite, g: Grupos, pos: LatLng | null) {
  g.marca.clearLayers();
  if (pos) L.circleMarker(pos, { radius: 15, weight: 3, color: "#ffffff", fill: false, interactive: false }).addTo(g.marca);
}
